import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  useTheme,
} from "@mui/material";
import { MouseEventHandler } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import DialogBody from "~/components/me/bento/content/committed-database/DialogBody";
import FormListItem from "~/components/me/bento/content/committed-database/FormListItem";
import { buildLatestRespondedForms } from "~/components/me/bento/content/committed-database/lib";
import { useMeCommittedDatabase } from "~/components/me/bento/lib";
import { useOpenContentModal } from "~/components/me/hooks";
import { flexRow } from "~/lib/client/design";
import { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import { MeCommittedDatabaseQuery, Sdk } from "~/lib/graphql/client";

const contentName = "CommittedDatabase";

const CommittedDatabase = ({ client }: { client: Sdk }) => {
  const result = useMeCommittedDatabase(client);
  const theme = useTheme();

  const { openedContent, openContentModal, closeContentModal } =
    useOpenContentModal();

  const isOpen = openedContent === contentName && result.status === "success";
  const openModal = () => {
    if (result.status !== "success") return;
    openContentModal(contentName);
  };

  const closeModal = () => closeContentModal();

  return (
    <>
      {match(result)
        .with({ status: "pending" }, () => <Loading />)
        .with({ status: "error" }, (matched) => (
          <RetriableError reload={matched.refetch} />
        ))
        .with({ status: "success" }, (matched) => (
          <Body content={matched.data} onClick={openModal} />
        ))
        .exhaustive()}
      {result.status === "success" && (
        <Dialog
          open={isOpen}
          onClose={closeModal}
          fullWidth
          PaperProps={{
            sx: {
              width: "100%",
              height: "80vh",
              m: 1,
            },
          }}
          maxWidth={"md"}
        >
          <DialogContent>
            <IconButton onClick={closeModal}>
              <CloseIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <DialogBody content={result.data} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const Body = ({
  content,
  onClick,
}: {
  content: MeCommittedDatabaseQuery;
  onClick: MouseEventHandler;
}) => {
  const theme = useTheme();

  const responses = content.me.responses;
  return (
    <ContentBodyLayout onClick={onClick} label={"Database | Commit"}>
      <p css={responsesLengthStyle}>
        <span>投稿数</span>
        <span>{responses.length}</span>
      </p>
      <div css={formsContainerStyle(theme)} role={"listbox"}>
        {buildLatestRespondedForms(responses)
          .slice(0, 3)
          .map((form, index) => (
            <FormListItem {...form} key={index} />
          ))}
      </div>
    </ContentBodyLayout>
  );
};

const responsesLengthStyle = css`
  ${flexRow};
  justify-content: flex-end;
  align-items: center;
  margin: 0 1rem 0 0;
  gap: 0.25rem;

  span:first-of-type {
    font-size: 0.75rem;
  }

  @media (max-width: ${SP_MODE_WIDTH}px) {
    margin: -1rem 0.5rem 0 0;
  }
`;

const formsContainerStyle = (theme: Theme) => css`
  display: flex;
  margin-top: 0.5rem;
  margin-left: 1rem;
  margin-right: 1rem;
  flex-direction: column;
  padding-right: 1rem;
  padding-left: 1rem;
  background: ${theme.palette.background.default};
  border-radius: 20px;

  a {
    border-bottom: 1px solid ${theme.palette.divider};

    &:last-child {
      border-bottom: none;
    }
  }

  @media (max-width: ${SP_MODE_WIDTH}px) {
    margin-left: 0.25rem;
    margin-right: 0.25rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
`;

export default CommittedDatabase;
